import FieldEquationForm from './field-equation-form'
import i18n from 'scoreboard/components/i18n'
import $ from 'jquery'
import _ from 'lodash'
import unwrapModel from 'scoreboard/components/unwrap-model'
import Dataset from 'scoreboard/datasets/model/dataset'
const { isTotal } = Dataset

const DataSourceFieldEquationForm = FieldEquationForm.extend({

	/**
	 * General form used to edit equations for data sources (see
	 * data-source-form.js)
	 *
	 * @constructs
	 *
	 * @param {FieldEquationFormOptions} options
	 *
	 * @param {Object} options.dataSourceAggregationType the aggregation type
	 * of the data source being edited
	 *
	 * @param {Object} options.datasets all datasets the user has access to
	 *
	 * @param {Boolean} [options.hideAggregationTypeForSameDataset=false] whether
	 * to hide the aggregation type dropdown when modifying a reference to the
	 * same dataset as the main dataset
	 */
	initialize: function(options = {}) {
		FieldEquationForm.prototype.initialize.apply(this, arguments)
		this.hideAggregationTypeForSameDataset = _.get(options,
			'hideAggregationTypeForSameDataset', false)
	},

	initializeAggregationIndexes: function() {
		if (_.isNil(this.options?.data)) return

		this._initializeAggregationIndexes(this.options.data.aggregations)
	},

	events: _.extend({}, FieldEquationForm.prototype.events, {
		'change [name=datasetId], [name=fieldId]': 'toggleAggregationType'
	}),

	render: function() {
		FieldEquationForm.prototype.render.apply(this, arguments)
		// #31957 we can't cut/paste when working with aggregations
		this.equationEditor.disableCutPaste()
		return this
	},

	context: function() {
		let context = FieldEquationForm.prototype.context.apply(this, arguments)
		context.title = i18n.get('scorecards.node.metric.equation.set')
		return context
	},

	isSameDataset: function() {
		// we're not coming from editing a specific dataset, so we always want this to
		// return false, because the user selects whatever dataset they want from the
		// tree selector, so there is no concept of "same" dataset
		return false
	},

	/**
	 * We always need an aggregation for a data source that isn't focused around a particular
	 * dataset. This logic was previously being handled by {@link #isSameDataset} above,
	 * but {@link FieldEquationValidator#needsAggregation} checks isSameDataset for itself
	 * and we don't want to not pass a startingDataset into that method as a hack to get
	 * the correct result.
	 * @returns {boolean}
	 */
	needsAggregation: function() {
		return true
	},

	/**
	 * #60758 matches and aggregations will map 1:1 for data source field equations,
	 * so if we're looping through aggregations by index we can assume the
	 * passed in match and aggregation are equal.
	 */
	isMatchEqualToAggregation: function(match, aggregation, matches) {
		if ((!_.isNil(match) && _.isNil(aggregation)) ||
			(_.isNil(match) && !_.isNil(aggregation))) return false

		// if we don't have an aggregationId, then we can assume the matches
		// and aggregations are in the same order
		return this.aggregations.length === matches.length
	},

	_referenceData: function(el) {
		// #59033 always read the datasetId from the DOM first
		let data = {
			datasetId: this.getDatasetId()
		}
		data = FieldEquationForm.prototype._referenceData.apply(this, [el, data])
		return this.getReference(data)
	},

	getReference: function(data = {}) {
		data = FieldEquationForm.prototype.getReference.apply(this, arguments)
		// we're always going to be dealing with fields on data-source-field-equation-form
		data = _.omit(data, 'columnOrField')
		const dataset = this.datasetsById[data.datasetId]
		if (_.isNil(dataset)) return data

		data.dataset = unwrapModel(dataset)?.label
		// can't use Dataset.combinedFieldsById because we don't store dataset models
		// on this.datasetsById
		const fieldsById = _.keyBy(dataset?.fields, 'id')
		const field = fieldsById[data?.fieldId]
		if (_.isNil(field)) return data

		data.field = unwrapModel(field)?.label

		data.aggregationType = FieldEquationForm.prototype
			.getCurrentOrDefaultAggregationType.apply(this, [data])

		return data
	},

	/**
	 * Initializes the equation editor form GUI controls for editing the
	 * selected reference, e.g. M(1)
	 *
	 * @param match the current match
	 * @param matches all the matches in the equation
	 * @param matchIndex the index of the match within the matches
	 */
	edit: function(match, matches, matchIndex) {
		FieldEquationForm.prototype.edit.apply(this, arguments)
		this.toggleAggregationType()
	},

	/**
	 * toggle the visibility of the aggregation type dropdown as appropriate
	 */
	toggleAggregationType: function() {
		let hideAggregationType = this.isHideAggregationType()
		this.$('.aggregation-type').toggleClass('is-not-displayed',
			hideAggregationType)
		this.$('.choose-source-column-or-field').toggleClass(
			'has-aggregation-type', !hideAggregationType)
	},

	isHideAggregationType: function() {
		if (!this.isShowingOnlyFields()) return true

		return FieldEquationForm.prototype.isHideAggregationType.call(this) ||
			this.isHideAggregationTypeForSameDataset()
	},

	isHideAggregationTypeForSameDataset: function() {
		// #60212 use the super method to check if the dataset is the same
		// since we will want to hide the aggregation type if there is no
		// way to select a different dataset
		const isSameDataset =
			FieldEquationForm.prototype.isSameDataset.apply(this, arguments)
		return this.hideAggregationTypeForSameDataset && isSameDataset
	},

	needsSpecifiedAggregationType: function(fieldId) {
		const needsAggType = FieldEquationForm.prototype
			.needsSpecifiedAggregationType.apply(this, arguments)
		if (!needsAggType) return false

		// total always needs 'sum' aggregationType
		if (isTotal(fieldId)) return true

		return !this.isHideAggregationTypeForSameDataset()
	},

	createEquationFormatter: function() {
		let dataset = this.model
		if (_.isEmpty(this.options.startingDatasetId)) {
			dataset = undefined
		}
		return new this.equationFormatter({ dataset })
	},

	// The none aggregation type doesn't make sense for calculated value widgets
	isOnlyNoneAggregation: _.constant(false),

	submit: function() {
		const shouldSubmit = this.submitGuard()
		if (!shouldSubmit) {
			return
		}
		let equation = this.equationEditor.getCleanEquation()
		// #37922 if there's no equation, no need to call the API
		if (!equation) {
			this._submitSuccess()
			return
		}

		$.post({
			...this._getUrlAndDataForSubmit(),
			success: () => this._submitSuccess(),
			error: this.onInvalidEquation.bind(this)
		})
	},

	_getUrlAndDataForSubmit: function() {
		return {
			url: 'api/datasets/validate-field-equation?' +
				$.param({ startingDatasetId: this.options.startingDatasetId }),
			data: JSON.stringify({
				field: {
					dataType: this.options.dataType,
					equation: this.equationEditor.getCleanEquation(),
					aggregations: this.aggregations
				}
			})
		}
	},

	_submitSuccess: function() {
		this.trigger('done', {
			equation: this.equationEditor.getCleanEquation(),
			aggregations: this.aggregations,
			treeItemsById: this.treeItemsById,
			datasetsById: this.datasetsById
		})
	}

})

export default DataSourceFieldEquationForm

DataSourceFieldEquationForm.__filename__ = 'datasets/data-source-field-equation-form.js'
